import React, { useState, useEffect, useMemo } from 'react';
import { workflowStagesService, tasksService, communicationsService, clientsService, quotesService } from '../../services';

// ─── Config ─────────────────────────────────────────────────────────────────

const PERIODS = [
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '365', label: 'Last year' },
];

const STAGE_COLORS = ['#6366F1', '#2563EB', '#059669', '#C4871F', '#DC2626', '#0D9488', '#9333EA', '#7C3AED'];

const PRIORITY_COLORS = { urgent: '#9333EA', high: '#DC2626', medium: '#C4871F', low: '#6B7A94' };
const STATUS_COLORS = { pending: '#C4871F', in_progress: '#2563EB', completed: '#059669', cancelled: '#6B7A94' };
const COMM_TYPE_META = {
  email: { label: 'Email', icon: '✉️', color: '#2563EB' },
  phone: { label: 'Phone Call', icon: '📞', color: '#059669' },
  meeting: { label: 'Meeting', icon: '🤝', color: '#C4871F' },
  text: { label: 'Text', icon: '💬', color: '#7C3AED' },
  quote_message: { label: 'Quote Message', icon: '📄', color: '#EA580C' },
  other: { label: 'Note', icon: '📝', color: '#6B7A94' },
};

function fmtCurrency(n) {
  return (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// ─── Chart components ───────────────────────────────────────────────────────

function HBar({ items, total, unitLabel = 'clients' }) {
  return (
    <div className="space-y-3">
      {items.map(item => {
        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-xs font-medium text-[var(--foreground)]">{item.label}</span>
              </div>
              <div className="flex items-center gap-2 text-right">
                <span className="text-xs text-[var(--muted-foreground)]">{item.count} {unitLabel}</span>
                <span className="text-xs font-bold w-9 text-right" style={{ color: item.count > 0 ? item.color : 'var(--muted-foreground)' }}>{pct}%</span>
              </div>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: item.color, minWidth: item.count > 0 ? '4px' : '0' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CommBar({ items, total }) {
  const max = Math.max(...items.map(i => i.count), 1);
  return (
    <div className="space-y-2.5">
      {items.map(item => {
        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
        return (
          <div key={item.label} className="flex items-center gap-3">
            <span className="text-base flex-shrink-0 w-5 text-center">{item.icon}</span>
            <span className="text-xs font-medium text-[var(--foreground)] w-28 flex-shrink-0">{item.label}</span>
            <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(item.count / max) * 100}%`, backgroundColor: item.color, minWidth: item.count > 0 ? '4px' : '0' }} />
            </div>
            <span className="text-xs font-bold w-16 text-right flex-shrink-0" style={{ color: item.count > 0 ? item.color : 'var(--muted-foreground)' }}>
              {item.count} <span className="font-normal text-[var(--muted-foreground)]">({pct}%)</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ items, size = 120 }) {
  const total = items.reduce((s, i) => s + i.count, 0);
  const r = 42, cx = 60, cy = 60, circumference = 2 * Math.PI * r;
  let offset = 0;
  const arcs = items.map(item => {
    const frac = total > 0 ? item.count / total : 0;
    const arc = { ...item, frac, dashArray: frac * circumference, startOffset: offset };
    offset += frac * circumference;
    return arc;
  });

  return (
    <div className="flex items-center gap-5">
      <svg width={size} height={size} viewBox="0 0 120 120" className="flex-shrink-0">
        {total === 0 ? (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth="14" />
        ) : arcs.map((arc, i) => arc.count > 0 && (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={arc.color} strokeWidth="14"
            strokeDasharray={`${arc.dashArray} ${circumference}`} strokeDashoffset={-arc.startOffset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '60px 60px', transition: 'stroke-dasharray .7s ease' }} />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--foreground)" style={{ fontFamily: 'var(--font-display)' }}>{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="var(--muted-foreground)">total</text>
      </svg>
      <div className="flex-1 space-y-2 min-w-0">
        {items.map(item => {
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-[var(--muted-foreground)] truncate">{item.label}</span>
              </div>
              <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: item.count > 0 ? item.color : 'var(--muted-foreground)' }}>
                {item.count} <span className="font-normal text-[var(--muted-foreground)]">({pct}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityChart({ points }) {
  const max = Math.max(...points.map(p => p.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-20">
      {points.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div className="w-full rounded-t-lg transition-all duration-700 relative group"
            style={{ height: `${(p.count / max) * 56}px`, backgroundColor: 'var(--primary)', minHeight: '3px' }}>
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10" style={{ backgroundColor: 'var(--primary)' }}>
              {p.count}
            </div>
          </div>
          <span className="text-[8px] text-[var(--muted-foreground)] truncate w-full text-center">{p.label}</span>
        </div>
      ))}
    </div>
  );
}

function ProgressBar({ value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color, minWidth: value > 0 ? '4px' : '0' }} />
      </div>
      <span className="text-xs font-bold w-9 text-right flex-shrink-0" style={{ color: value > 0 ? color : 'var(--muted-foreground)' }}>{pct}%</span>
    </div>
  );
}

// ─── Data processing ────────────────────────────────────────────────────────

function computeAvgResponseHours(communications) {
  const byClient = {};
  for (const c of communications) {
    if (!c.client_id) continue;
    (byClient[c.client_id] ||= []).push(c);
  }
  const deltas = [];
  for (const clientComms of Object.values(byClient)) {
    const sorted = [...clientComms].sort((a, b) => new Date(a.communication_date) - new Date(b.communication_date));
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].direction === 'inbound' && sorted[i + 1].direction === 'outbound') {
        const hrs = (new Date(sorted[i + 1].communication_date) - new Date(sorted[i].communication_date)) / 36e5;
        if (hrs >= 0) deltas.push(hrs);
      }
    }
  }
  if (deltas.length === 0) return 0;
  return Math.round((deltas.reduce((s, d) => s + d, 0) / deltas.length) * 10) / 10;
}

function buildActivityPoints(communications, days) {
  // Weekly buckets for shorter windows, monthly buckets for the 1-year view
  const now = new Date();
  const buckets = [];
  if (days <= 90) {
    const weeks = Math.ceil(days / 7);
    for (let i = weeks - 1; i >= 0; i--) {
      const end = new Date(now); end.setDate(end.getDate() - i * 7);
      const start = new Date(end); start.setDate(start.getDate() - 6);
      buckets.push({ start, end, label: start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count: 0 });
    }
  } else {
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ start: d, end: new Date(now.getFullYear(), now.getMonth() - i + 1, 0), label: d.toLocaleDateString('en-US', { month: 'short' }), count: 0 });
    }
  }
  for (const c of communications) {
    const date = new Date(c.communication_date);
    const bucket = buckets.find(b => date >= b.start && date <= b.end);
    if (bucket) bucket.count++;
  }
  return buckets.map(({ label, count }) => ({ label, count }));
}

const WorkflowAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30');
  const [raw, setRaw] = useState({ stages: [], tasks: [], communications: [], clients: [], quotes: [], commStats: {} });

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(period, 10));

      const [stages, tasks, communications, clients, quotes, commStats] = await Promise.all([
        workflowStagesService.getStages(),
        tasksService.getTasks(),
        communicationsService.getCommunications({
          date_from: startDate.toISOString().split('T')[0],
          date_to: endDate.toISOString().split('T')[0],
        }),
        clientsService.getAll(),
        quotesService.getAll(),
        communicationsService.getCommunicationStats(),
      ]);

      setRaw({ stages, tasks, communications, clients, quotes, commStats, startDate, endDate });
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const data = useMemo(() => {
    const { stages, tasks, communications, clients, quotes, commStats, startDate, endDate } = raw;

    const clientsByStage = stages.map((stage, i) => ({
      label: stage.name,
      color: STAGE_COLORS[i % STAGE_COLORS.length],
      count: clients.filter(c => c.workflow_stage_id === stage.id).length,
    }));

    const tasksByPriority = ['urgent', 'high', 'medium', 'low'].map(p => ({
      label: p.charAt(0).toUpperCase() + p.slice(1), color: PRIORITY_COLORS[p],
      count: tasks.filter(t => t.priority === p).length,
    }));

    const tasksByStatus = ['pending', 'in_progress', 'completed', 'cancelled'].map(s => ({
      label: s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1), color: STATUS_COLORS[s],
      count: tasks.filter(t => t.status === s).length,
    }));

    const commTypeKeys = Object.keys(COMM_TYPE_META);
    const commsByType = commTypeKeys.map(type => ({
      label: COMM_TYPE_META[type].label, icon: COMM_TYPE_META[type].icon, color: COMM_TYPE_META[type].color,
      count: communications.filter(c => c.communication_type === type).length,
    }));

    const stagePerformance = stages.map((stage, i) => {
      const stageTasks = tasks.filter(t => t.workflow_stage_id === stage.id);
      return {
        stage: stage.name, color: STAGE_COLORS[i % STAGE_COLORS.length],
        clients: clients.filter(c => c.workflow_stage_id === stage.id).length,
        total: stageTasks.length, completed: stageTasks.filter(t => t.status === 'completed').length,
      };
    });

    // Top clients by interaction (comms + tasks)
    const clientMap = new Map(clients.map(c => [c.id, c]));
    const commsByClient = {};
    for (const c of communications) commsByClient[c.client_id] = (commsByClient[c.client_id] || 0) + 1;
    const tasksByClient = {};
    for (const t of tasks) if (t.client_id) tasksByClient[t.client_id] = (tasksByClient[t.client_id] || 0) + 1;
    const topClients = Object.entries(commsByClient)
      .map(([clientId, comms]) => {
        const client = clientMap.get(Number(clientId));
        const stage = stages.find(s => s.id === client?.workflow_stage_id);
        return {
          name: client ? `${client.first_name} ${client.last_name}` : 'Unknown client',
          comms, tasks: tasksByClient[clientId] || 0, stage: stage?.name || 'No stage',
        };
      })
      .sort((a, b) => b.comms - a.comms)
      .slice(0, 7);

    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const acceptedRevenue = quotes
      .filter(q => q.status === 'accepted' && startDate && endDate && new Date(q.created_at) >= startDate && new Date(q.created_at) <= endDate)
      .reduce((sum, q) => sum + (Number(q.total_amount) || 0), 0);

    const avgResponseHrs = computeAvgResponseHours(communications);
    const emailCount = commsByType.find(c => c.label === 'Email')?.count || 0;
    const outboundRatioPct = communications.length > 0 ? Math.round((emailCount / communications.length) * 100) : 0;

    return {
      totalClients: clients.length, totalStages: stages.length,
      clientsByStage, tasksByPriority, tasksByStatus, commsByType, stagePerformance, topClients,
      totalTasks, completionRate, totalComms: communications.length,
      activityPoints: buildActivityPoints(communications, parseInt(period, 10)),
      avgResponseHrs, outboundRatioPct, emailCount,
      overdueFollowUps: commStats.overdue_followups || 0,
      totalRevenue: acceptedRevenue,
    };
  }, [raw, period]);

  if (loading && raw.clients.length === 0 && !error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--accent)' }}></div>
          <p className="mt-4 text-sm text-[var(--muted-foreground)]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Total Clients', value: data.totalClients.toString(), sub: `${data.totalStages} active stages`, color: '#6366F1', bg: 'rgba(99,102,241,0.1)',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
    { label: 'Task Completion', value: `${data.completionRate}%`, sub: `${data.totalTasks} total tasks`, color: '#059669', bg: 'rgba(5,150,105,0.1)',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },
    { label: 'Communications', value: data.totalComms.toString(), sub: `Avg ${data.avgResponseHrs}h response`, color: '#2563EB', bg: 'rgba(37,99,235,0.1)',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg> },
    { label: 'Est. Revenue', value: fmtCurrency(data.totalRevenue), sub: `${data.overdueFollowUps} overdue follow-ups`, color: '#C4871F', bg: 'rgba(196,135,31,0.1)',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> },
  ];

  const summaryMetrics = [
    { label: 'Avg Response Time', value: `${data.avgResponseHrs}h`, sub: 'Average time from inbound contact to reply', trend: data.avgResponseHrs > 0 && data.avgResponseHrs < 5 ? 'good' : 'warn',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg> },
    { label: 'Follow-up Rate', value: `${data.overdueFollowUps} overdue`, sub: 'Pending follow-ups requiring action', trend: data.overdueFollowUps === 0 ? 'good' : 'warn',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg> },
    { label: 'Email Share', value: `${data.outboundRatioPct}%`, sub: 'Email as share of all communications', trend: 'neutral',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg> },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>Workflow Analytics</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">Comprehensive insights into your workflow performance and client management.</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          className="px-4 py-2.5 text-sm font-semibold border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] cursor-pointer flex-shrink-0">
          {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(kpi => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-[var(--border)] p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: kpi.bg, color: kpi.color }}>{kpi.icon}</div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-foreground)] text-right leading-tight">{kpi.label}</span>
            </div>
            <p className="text-2xl font-black text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>{kpi.value}</p>
            <p className="text-[11px] text-[var(--muted-foreground)] mt-0.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[var(--foreground)]">Client Distribution by Stage</h2>
            <span className="text-xs text-[var(--muted-foreground)] font-semibold">{data.totalClients} clients</span>
          </div>
          <HBar items={data.clientsByStage} total={data.totalClients} />
        </div>
        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[var(--foreground)]">Task Priority Distribution</h2>
            <span className="text-xs text-[var(--muted-foreground)] font-semibold">{data.totalTasks} tasks</span>
          </div>
          <DonutChart items={data.tasksByPriority} />
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[var(--foreground)]">Communication Types</h2>
            <span className="text-xs text-[var(--muted-foreground)] font-semibold">{data.totalComms} total</span>
          </div>
          <CommBar items={data.commsByType} total={data.totalComms} />
        </div>
        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[var(--foreground)]">Task Status Overview</h2>
            <span className="text-xs text-[var(--muted-foreground)] font-semibold">{data.totalTasks} tasks</span>
          </div>
          <DonutChart items={data.tasksByStatus} />
        </div>
      </div>

      {/* Activity chart */}
      <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-bold text-[var(--foreground)]">Communication Activity</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">Volume over selected period</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'var(--primary)' }} />
            Communications logged
          </div>
        </div>
        <ActivityChart points={data.activityPoints} />
      </div>

      {/* Stage performance table */}
      <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h2 className="text-sm font-bold text-[var(--foreground)]">Stage Performance</h2>
          <p className="text-xs text-[var(--muted-foreground)] mt-0.5">Task completion rates per workflow stage</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--muted)' }}>
                {['Stage', 'Clients', 'Total Tasks', 'Completed', 'Completion Rate'].map(col => (
                  <th key={col} className="px-5 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[var(--muted-foreground)]">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {data.stagePerformance.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-[var(--muted-foreground)]">No workflow stages configured yet.</td></tr>
              ) : data.stagePerformance.map(row => (
                <tr key={row.stage} className="hover:bg-[var(--muted)]/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
                      <span className="text-sm font-semibold text-[var(--foreground)]">{row.stage}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5"><span className="text-sm font-bold" style={{ color: row.clients > 0 ? row.color : 'var(--muted-foreground)' }}>{row.clients}</span></td>
                  <td className="px-5 py-3.5 text-sm text-[var(--muted-foreground)]">{row.total}</td>
                  <td className="px-5 py-3.5 text-sm text-[var(--muted-foreground)]">{row.completed}</td>
                  <td className="px-5 py-3.5" style={{ minWidth: '200px' }}><ProgressBar value={row.completed} total={row.total} color={row.color} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top clients */}
      <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h2 className="text-sm font-bold text-[var(--foreground)]">Top Clients by Interaction</h2>
          <p className="text-xs text-[var(--muted-foreground)] mt-0.5">Ranked by total communications logged</p>
        </div>
        {data.topClients.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-[var(--muted-foreground)]">No communications logged in this period yet.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {data.topClients.map((client, i) => {
              const maxComms = data.topClients[0].comms;
              return (
                <div key={client.name + i} className="flex items-center gap-4 px-5 py-3.5 hover:bg-[var(--muted)]/20 transition-colors">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                    style={i === 0 ? { backgroundColor: 'rgba(196,135,31,0.15)', color: '#C4871F' }
                      : i === 1 ? { backgroundColor: 'rgba(107,122,148,0.15)', color: '#6B7A94' }
                      : i === 2 ? { backgroundColor: 'rgba(180,83,9,0.12)', color: '#B45309' }
                      : { backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                    {i + 1}
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }}>
                    {client.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[var(--foreground)] truncate">{client.name}</p>
                    <p className="text-xs text-[var(--muted-foreground)]">{client.stage}</p>
                  </div>
                  <div className="flex items-center gap-5 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">Comms</p>
                      <p className="text-sm font-black" style={{ color: 'var(--primary)', fontFamily: 'var(--font-display)' }}>{client.comms}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">Tasks</p>
                      <p className="text-sm font-black" style={{ color: 'var(--accent)', fontFamily: 'var(--font-display)' }}>{client.tasks}</p>
                    </div>
                    <div className="w-24 h-2 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: 'var(--muted)' }}>
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(client.comms / maxComms) * 100}%`, backgroundColor: 'var(--primary)' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Performance summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryMetrics.map(metric => (
          <div key={metric.label} className="bg-white rounded-2xl border border-[var(--border)] p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={metric.trend === 'good' ? { backgroundColor: 'rgba(5,150,105,0.1)', color: '#059669' }
                  : metric.trend === 'warn' ? { backgroundColor: 'rgba(220,38,38,0.08)', color: '#DC2626' }
                  : { backgroundColor: 'rgba(196,135,31,0.1)', color: '#C4871F' }}>
                {metric.icon}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-foreground)]">{metric.label}</span>
            </div>
            <p className="text-xl font-black text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>{metric.value}</p>
            <p className="text-[11px] text-[var(--muted-foreground)] mt-1 leading-relaxed">{metric.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowAnalytics;
